import { Injectable } from '@angular/core';
import { fliFxStampKeyOf } from '@modeso/aumico-lib-notes-fe';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    AccountMappingAccountItem,
    AccountMappingTemplateGroupItem,
    IAccount,
    IAccountMappingSubFLI,
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IManualAccount,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { TemplateType } from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
import { IFLIForNotes } from '../../../../../../aumico-lib-notes-fe/src/lib/interfaces/IFLIForNotes.interface';
import { AccountsHelper } from '../helpers/accounts.helper';
import { IFxNodeStampValues } from '../utils/FxTranslationHelper';

const debug = Debug('aumico:saas:FlisMapper');

@Injectable({ providedIn: 'root' })
export class FlisMapper {
    private mockedPosition: number;

    private flisOfItems: IFLIForNotes[];

    /**
     * FX (REV-21512): while walking, collect the `__fx` stamps of the template accounts under the
     * same identity the FLI table rows carry (`id|accountName`). The stamps must NEVER live on the
     * IManualAccount literals themselves — the FLI table form-controls every field of an account
     * row and would silently persist them into the notes blob.
     */
    private fxCollector: Map<string, IFxNodeStampValues> | null = null;

    /** Keys two DIFFERENT stamps were written under — neither row may claim the other's figure. */
    private fxCollisions = new Set<string>();

    getFlisOfBalanceAndIncomeTemplates(
        balanceTemplate: IAccountMappingTemplate,
        incomeTemplate: IAccountMappingTemplate,
        fxCollector?: Map<string, IFxNodeStampValues>,
    ): IFLIForNotes[] {
        this.mockedPosition = 0;
        this.flisOfItems = [];
        this.fxCollector = fxCollector ?? null;
        this.fxCollisions = new Set();

        const flisWithAccounts: IFLIForNotes[] = [];

        const balanceFlisWithAccounts: IFLIForNotes[] = this.getFlisOfTemplate(balanceTemplate);

        const incomeFlisWithAccounts: IFLIForNotes[] = this.getFlisOfTemplate(incomeTemplate);

        flisWithAccounts.push(...balanceFlisWithAccounts, ...incomeFlisWithAccounts);
        debug(flisWithAccounts);
        return this.sortFlisByPosition(flisWithAccounts);
    }

    getFlisOfTemplate(template: IAccountMappingTemplate): IFLIForNotes[] {
        const flisOfGroups: IFLIForNotes[] = [];

        const templateGroups: IAccountMappingTemplateGroup[] = [template.group];

        flisOfGroups.push(...this.getFlisOfGroups(templateGroups, template.type));

        return flisOfGroups;
    }

    private getFlisOfGroups(
        templateGroups: IAccountMappingTemplateGroup[],
        templateType: TemplateType,
    ): IFLIForNotes[] {
        const flisOfGroups: IFLIForNotes[] = [];
        for (const group of templateGroups) {
            // Use sortedItems (transformed data with split accounts), fallback to items if not available
            const itemsToProcess = group.sortedItems || group.items;
            if (itemsToProcess) {
                const flisOfCurrentGroup: IFLIForNotes[] = this.extractFLIAndAccounts(itemsToProcess, templateType);
                flisOfGroups.push(...flisOfCurrentGroup);

                // Recurse into nested groups within sortedItems
                for (const item of itemsToProcess) {
                    if (item.sortedItems) {
                        flisOfGroups.push(...this.getFlisOfGroups([item], templateType));
                    } else if (item.groups && item.groups.length > 0) {
                        // this is a fallback if sortedItems is not available
                        flisOfGroups.push(...this.getFlisOfGroups(item.groups, templateType));
                    }
                }
            }
        }

        return flisOfGroups;
    }

    private extractFLIAndAccounts(
        itemsOfGroup: AccountMappingTemplateGroupItem[],
        templateType: TemplateType,
    ): IFLIForNotes[] {
        this.flisOfItems = [];
        for (const item of itemsOfGroup) {
            if (item.type === ItemType.FLI) {
                let totalCurrentValue = 0;
                let totalPreviousValue = 0;
                // REV-20899: the displayed FLI total is the calculated balance
                // PLUS the aggregated reclass. `recalculateGroupSummaries` has
                // already summed children's `closingReClass` / `openingReClass`
                // up to this node, so reading them here is enough.
                const closingReClass =
                    item.closingReClass != null && NumberUtils.isFinite(item.closingReClass)
                        ? item.closingReClass
                        : 0;
                const openingReClass =
                    item.openingReClass != null && NumberUtils.isFinite(item.openingReClass)
                        ? item.openingReClass
                        : 0;
                if (item.calculatedClosingBalance != null && NumberUtils.isFinite(item.calculatedClosingBalance)) {
                    totalCurrentValue = NumberUtils.roundNumber(item.calculatedClosingBalance + closingReClass);
                } else if (closingReClass !== 0) {
                    totalCurrentValue = NumberUtils.roundNumber(closingReClass);
                }

                if (item.calculatedOpeningBalance != null && NumberUtils.isFinite(item.calculatedOpeningBalance)) {
                    totalPreviousValue = NumberUtils.roundNumber(item.calculatedOpeningBalance + openingReClass);
                } else if (openingReClass !== 0) {
                    totalPreviousValue = NumberUtils.roundNumber(openingReClass);
                }
                const accountsOfFli = this.getAccountsOfFli(item.accounts, templateType);
                // REV-20899: append user-created phantom reclasses accounts so
                // they appear in notes/AoP/EMS dropdowns and rows alongside
                // imported accounts. Stored on `fli.reclassesAccounts`; their
                // displayed CY/PY equals only their net reclass sum (imported
                // balance is zero).
                const phantomAccounts = this.getPhantomAccountsFromReclasses((item as any).reclassesAccounts);
                const fli: IFLIForNotes = {
                    id: item.name,
                    caption: item.caption,
                    position: this.mockedPosition,
                    accounts: [...accountsOfFli, ...phantomAccounts],
                    totalRow: {
                        accountName: undefined,
                        currentValue: totalCurrentValue,
                        previousValue: totalPreviousValue,
                    },
                    type: item.fliType,
                    itemType: item.type,
                    belongingTemplateType: templateType,
                };
                this.mockedPosition++;
                this.flisOfItems.push(fli);
            }
        }

        return this.flisOfItems;
    }

    private getAccountsOfFli(accounts: AccountMappingAccountItem[], templateType: TemplateType): IManualAccount[] {
        const baseAccounts: IManualAccount[] = [];
        for (const account of accounts) {
            if (account.type === ItemType.SUB_FLI || account.type === ItemType.OF_THAT_ITEM) {
                this.extractSubFliAndAccounts(account, templateType);
                const accountsOfSubFli: IManualAccount[] = this.getAccountsOfFli(account.accounts, templateType);
                baseAccounts.push(...accountsOfSubFli);
                continue;
            }
            if (account.type === ItemType.EMPTY || account.type === ItemType.HEADER) {
                continue;
            }

            const roundedAccount = AccountsHelper.roundAccountsValues([account as IAccount])[0];

            // REV-20899: per-account displayed values include the leaf account's
            // own reclass (stamped earlier by
            // `applyReclassificationsToFinStatements` from the transactions list).
            const accountClosingReClass =
                roundedAccount.closingReClass != null && NumberUtils.isFinite(roundedAccount.closingReClass)
                    ? roundedAccount.closingReClass
                    : 0;
            const accountOpeningReClass =
                roundedAccount.openingReClass != null && NumberUtils.isFinite(roundedAccount.openingReClass)
                    ? roundedAccount.openingReClass
                    : 0;

            let previousValue = 0;
            if (roundedAccount.calculatedOpeningBalance != null) {
                previousValue = roundedAccount.calculatedOpeningBalance + accountOpeningReClass;
            } else if (accountOpeningReClass !== 0) {
                previousValue = accountOpeningReClass;
            }

            let currentValue = 0;

            if (roundedAccount.calculatedClosingBalance != null) {
                currentValue = roundedAccount.calculatedClosingBalance + accountClosingReClass;
            } else if (accountClosingReClass !== 0) {
                currentValue = accountClosingReClass;
            }

            const baseAccount: IManualAccount = {
                accountName: account.name,
                id: !isNaN(+(account as IAccount).accountNumber)
                    ? +(account as IAccount).accountNumber
                    : (account as IAccount).accountNumber,
                previousValue,
                currentValue,
            };
            // The leg of a balance-switch account travels with the row: it is the only thing
            // telling the two legs apart, and the saved note row needs it to find its own stamp.
            const leg = account as { isBalanceSwitchAccount?: boolean; originsFromAsset?: boolean };
            if (leg.isBalanceSwitchAccount === true && typeof leg.originsFromAsset === 'boolean') {
                (baseAccount as IManualAccount & { originsFromAsset?: boolean }).originsFromAsset =
                    leg.originsFromAsset;
            }

            this.collectFxStamp(fliFxStampKeyOf(baseAccount), (account as { __fx?: IFxNodeStampValues }).__fx);

            baseAccounts.push(baseAccount);
        }
        return baseAccounts;
    }

    private extractSubFliAndAccounts(subFli: IAccountMappingSubFLI, templateType: TemplateType) {
        this.mockedPosition++;
        // REV-20899: SubFLI totals must include the aggregated reclasses of
        // its child accounts. `recalculateGroupSummaries` has already summed
        // them onto `subFli.closingReClass` / `subFli.openingReClass`.
        const subFliClosingReClass =
            subFli.closingReClass != null && NumberUtils.isFinite(subFli.closingReClass)
                ? subFli.closingReClass
                : 0;
        const subFliOpeningReClass =
            subFli.openingReClass != null && NumberUtils.isFinite(subFli.openingReClass)
                ? subFli.openingReClass
                : 0;
        const subFliCurrentValue =
            subFli.calculatedClosingBalance != null
                ? subFli.calculatedClosingBalance + subFliClosingReClass
                : subFliClosingReClass !== 0
                  ? subFliClosingReClass
                  : subFli.calculatedClosingBalance;
        const subFliPreviousValue =
            subFli.calculatedOpeningBalance != null
                ? subFli.calculatedOpeningBalance + subFliOpeningReClass
                : subFliOpeningReClass !== 0
                  ? subFliOpeningReClass
                  : subFli.calculatedOpeningBalance;
        const accountsOfSubFli = this.getAccountsOfFli(subFli.accounts, templateType);
        // REV-20899: SubFLIs can also carry phantom reclasses accounts —
        // include them in the SubFLI's account list.
        const subFliPhantomAccounts = this.getPhantomAccountsFromReclasses((subFli as any).reclassesAccounts);
        const fli: IFLIForNotes = {
            id: subFli.name,
            caption: subFli.caption,
            position: this.mockedPosition,
            accounts: [...accountsOfSubFli, ...subFliPhantomAccounts],
            totalRow: {
                accountName: undefined,
                currentValue: subFliCurrentValue,
                previousValue: subFliPreviousValue,
            },
            belongingTemplateType: templateType,
            itemType: subFli.type,
            type: undefined,
        };
        this.flisOfItems.push(fli);
    }

    /**
     * REV-20899: converts entries from `fli.reclassesAccounts` (phantom
     * accounts the user created in grouping/masking) into `IManualAccount`
     * rows so they appear in the FLI's account list everywhere this mapper
     * is consumed (notes / AoP / EMS / equity-movement / etc).
     *
     * A phantom has no imported balance, so its displayed CY equals its net
     * `closingReClass` and its PY equals its `openingReClass`. Both values
     * have already been stamped onto the entry by
     * `applyReclassificationsToFinStatements` (the central reclass-applied
     * provider helper) from the saved transaction list.
     */
    private getPhantomAccountsFromReclasses(reclassesAccounts: unknown): IManualAccount[] {
        if (!Array.isArray(reclassesAccounts) || reclassesAccounts.length === 0) return [];
        const out: IManualAccount[] = [];
        for (const ra of reclassesAccounts as Array<any>) {
            if (ra == null || ra.accountNumber == null) continue;
            const closingReClass =
                ra.closingReClass != null && NumberUtils.isFinite(ra.closingReClass) ? ra.closingReClass : 0;
            const openingReClass =
                ra.openingReClass != null && NumberUtils.isFinite(ra.openingReClass) ? ra.openingReClass : 0;
            const phantomAccount: IManualAccount = {
                accountName: ra.name,
                id: !isNaN(+ra.accountNumber) ? +ra.accountNumber : ra.accountNumber,
                currentValue: NumberUtils.roundNumber(closingReClass),
                previousValue: NumberUtils.roundNumber(openingReClass),
            };
            this.collectFxStamp(fliFxStampKeyOf(phantomAccount), ra.__fx as IFxNodeStampValues | undefined);
            out.push(phantomAccount);
        }
        return out;
    }

    /**
     * A SubFLI's accounts are walked twice (by the SubFLI and by its parent) and carry the SAME
     * stamp object both times — that is not a collision. Two different stamps under one key are: a
     * leg without its side, a legacy duplicate. Then the key is dropped for good, so the cell stays
     * empty rather than showing the other row's figure.
     */
    private collectFxStamp(key: string, stamp: IFxNodeStampValues | undefined): void {
        if (stamp == null || this.fxCollector == null || this.fxCollisions.has(key)) {
            return;
        }
        const existing = this.fxCollector.get(key);
        if (existing != null && existing !== stamp) {
            this.fxCollector.delete(key);
            this.fxCollisions.add(key);

            return;
        }
        this.fxCollector.set(key, stamp);
    }

    private sortFlisByPosition(flis: IFLIForNotes[]): IFLIForNotes[] {
        return flis.sort((fli1, fli2) => (fli1.position < fli2.position ? -1 : 1));
    }
}
